import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { revertAll } from '../actions';

export interface IChatMessage {
    id: string;
    type: 'user' | 'bot';
    text: string;
    timestamp: string;
    /** The originating user query for a bot message; used to tailor rendering (e.g. date-only intent). */
    query?: string;
    /** Follow-up conversational prompt for bot messages. */
    followUp?: string | null;
}

const normalizeMessage = (message: IChatMessage): IChatMessage => ({
    ...message,
    timestamp: typeof message.timestamp === 'string' ? message.timestamp : new Date(message.timestamp).toISOString(),
});

export interface IChatbotState {
    sessionId: string | null;
    messages: IChatMessage[];
    isLoading: boolean;
    error: string | null;
    isDrawerOpen: boolean;
}

const initialState: IChatbotState = {
    sessionId: null,
    messages: [],
    isLoading: false,
    error: null,
    isDrawerOpen: false,
};

const chatbotSlice = createSlice({
    name: 'chatbot',
    initialState,
    reducers: {
        /**
         * Initialize session ID
         */
        initializeSession: (state, action: PayloadAction<string>) => {
            state.sessionId = action.payload;
        },

        /**
         * Add a message to the conversation
         */
        addMessage: (state, action: PayloadAction<IChatMessage>) => {
            state.messages.push(normalizeMessage(action.payload));
        },

        /**
         * Add multiple messages
         */
        addMessages: (state, action: PayloadAction<IChatMessage[]>) => {
            state.messages = [...state.messages, ...action.payload.map(normalizeMessage)];
        },

        /**
         * Clear all messages
         */
        clearMessages: state => {
            state.messages = [];
        },

        /**
         * Set loading state
         */
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },

        /**
         * Set error message
         */
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },

        /**
         * Reset error
         */
        resetError: state => {
            state.error = null;
        },

        /**
         * Toggle drawer open/close
         */
        setDrawerOpen: (state, action: PayloadAction<boolean>) => {
            state.isDrawerOpen = action.payload;
        },

        /**
         * Reset entire chatbot state
         */
        resetChatbot: () => initialState,
    },
    extraReducers: builder => builder.addCase(revertAll, () => initialState),
});

export const {
    initializeSession,
    addMessage,
    addMessages,
    clearMessages,
    setLoading,
    setError,
    resetError,
    setDrawerOpen,
    resetChatbot,
} = chatbotSlice.actions;

export const chatbotReducer = chatbotSlice.reducer;
